// @seventide/types — platform area: data rights (R-5.2, NFR Privacy, spec §13.2). Re-exported by ../platform.ts.
//
// Full workspace export (GDPR access + portability), workspace deletion with crypto-shredding and a signed
// deletion certificate, and account deletion. Shared by the API (validation) and the web (forms, typed
// confirmation).
import { z } from 'zod';
import { Id, IsoDateTime } from '../common';
import { Role } from '../enums';

/** `exports.type` of a full data export (GDPR). */
export const DATA_EXPORT_TYPE = 'gdpr_export';
/** The word people can type to confirm a deletion (instead of the workspace name). */
export const DELETE_CONFIRMATION_WORD = 'DELETE';
/** R-5.2 / §13.2: deletion completes within this many days of the request. */
export const DELETION_GUARANTEE_DAYS = 30;
/** Database backups roll off after this many days (DPIA §4.6) — stated on the certificate. */
export const BACKUP_RETENTION_DAYS = 35;
/** A scheduled deletion may be delayed by at most this many days (still within the 30-day guarantee). */
export const DELETION_MAX_DELAY_DAYS = 30;

const normalise = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Typed confirmation for workspace deletion: the workspace name or the word DELETE (case and extra spaces
 * ignored, so a person typing "delete" or "the hughes family" is not tripped up).
 */
export function isWorkspaceDeletionConfirmed(typed: string | null | undefined, workspaceName: string): boolean {
  if (!typed) return false;
  const t = normalise(typed);
  if (!t) return false;
  return t === normalise(DELETE_CONFIRMATION_WORD) || (normalise(workspaceName) !== '' && t === normalise(workspaceName));
}

/** Typed confirmation for account deletion: the word DELETE. */
export function isAccountDeletionConfirmed(typed: string | null | undefined): boolean {
  return Boolean(typed) && normalise(typed!) === normalise(DELETE_CONFIRMATION_WORD);
}

// ---------------------------------------------------------------- exports

export const DataExportStatus = z.enum(['pending', 'rendering', 'ready', 'failed']);
export type DataExportStatus = z.infer<typeof DataExportStatus>;

/** A previous export (GDPR ZIP or any pack) listed on More › Your data. */
export const DataExportDto = z.object({
  id: Id,
  type: z.string(),
  title: z.string(),
  format: z.enum(['pdf', 'csv', 'zip', 'json']),
  status: DataExportStatus,
  createdAt: IsoDateTime,
  completedAt: IsoDateTime.nullable(),
  sizeBytes: z.number().int().nullable(),
  filename: z.string().nullable(),
  /** Short-lived signed URL (≤5 min) when ready and the viewer may download it. */
  downloadUrl: z.string().nullable(),
  createdByName: z.string().nullable(),
});
export type DataExportDto = z.infer<typeof DataExportDto>;

export const DeletionRequestStatus = z.enum(['pending', 'cancelled', 'processing', 'completed', 'failed']);
export type DeletionRequestStatus = z.infer<typeof DeletionRequestStatus>;

export const DeletionRequestDto = z.object({
  id: Id,
  status: DeletionRequestStatus,
  requestedAt: IsoDateTime,
  scheduledFor: IsoDateTime,
  requestedByName: z.string().nullable(),
  /** The owner may cancel while it is still pending and in the future. */
  canCancel: z.boolean(),
});
export type DeletionRequestDto = z.infer<typeof DeletionRequestDto>;

/** GET /api/w/:id/platform/data — More › Your data. */
export const DataOverview = z.object({
  workspaceName: z.string(),
  role: Role,
  /** Owner, attorney (registered) or Principal: may download everything (spec §3.2 export packs). */
  canExport: z.boolean(),
  /** Owner only. */
  canDeleteWorkspace: z.boolean(),
  readOnly: z.boolean(),
  archive: z.boolean(),
  /** GDPR exports + every other pack (only for people who may export). */
  exports: z.array(DataExportDto),
  pendingDeletion: DeletionRequestDto.nullable(),
  guaranteeDays: z.number().int(),
  backupRetentionDays: z.number().int(),
});
export type DataOverview = z.infer<typeof DataOverview>;

export const RequestDataExportResponse = z.object({ export: DataExportDto });
export type RequestDataExportResponse = z.infer<typeof RequestDataExportResponse>;

// ---------------------------------------------------------------- workspace deletion

export const DeleteWorkspaceRequest = z.object({
  /** The workspace name or the word DELETE. */
  confirmation: z.string().max(300),
  /** 0 = delete now (default); otherwise schedule up to 30 days ahead (can be cancelled until then). */
  delayDays: z.number().int().min(0).max(DELETION_MAX_DELAY_DAYS).default(0),
});
export type DeleteWorkspaceRequest = z.input<typeof DeleteWorkspaceRequest>;

/** Signed certificate content (canonical JSON of everything except `sha256` is what is hashed and signed). */
export const DeletionCertificateDetails = z.object({
  schema: z.literal('seventide.deletion-certificate/1'),
  certificateId: Id,
  workspaceId: Id,
  workspaceLabel: z.string(),
  requestedAt: IsoDateTime.nullable(),
  scheduledFor: IsoDateTime.nullable(),
  deletedAt: IsoDateTime,
  /** Rows deleted per table. */
  tables: z.record(z.string(), z.number().int()),
  totalRows: z.number().int(),
  files: z.object({
    count: z.number().int(),
    bytes: z.number().int(),
    storageObjectsRemoved: z.number().int(),
    workspacePrefixRemoved: z.boolean(),
  }),
  encryptionKey: z.object({
    destroyed: z.boolean(),
    versionsDestroyed: z.number().int(),
    method: z.string(),
  }),
  verification: z.object({ rowsRemaining: z.number().int(), passed: z.boolean() }),
  backups: z.object({ retentionDays: z.number().int(), note: z.string() }),
  signer: z.object({ algorithm: z.literal('Ed25519'), publicKeyFingerprint: z.string() }),
  /** sha256 (hex) of the canonical JSON of the other fields. */
  sha256: z.string(),
});
export type DeletionCertificateDetails = z.infer<typeof DeletionCertificateDetails>;

export const DeletionCertificateDto = z.object({
  id: Id,
  workspaceLabel: z.string(),
  issuedAt: IsoDateTime,
  details: DeletionCertificateDetails,
  /** base64 Ed25519 signature over the canonical details (verify with GET /api/rules/public-key). */
  signature: z.string(),
  /** GET /api/me/deletion-certificates/:id/pdf */
  pdfUrl: z.string(),
});
export type DeletionCertificateDto = z.infer<typeof DeletionCertificateDto>;

export const DeleteWorkspaceResponse = z.object({
  /** completed = deleted now; scheduled = will be deleted on `request.scheduledFor`; retrying = a first attempt failed and the job will finish it. */
  status: z.enum(['completed', 'scheduled', 'retrying']),
  request: DeletionRequestDto.nullable(),
  certificate: DeletionCertificateDto.nullable(),
});
export type DeleteWorkspaceResponse = z.infer<typeof DeleteWorkspaceResponse>;

export const DeletionCertificateList = z.object({ items: z.array(DeletionCertificateDto) });
export type DeletionCertificateList = z.infer<typeof DeletionCertificateList>;

// ---------------------------------------------------------------- account deletion

export const DeleteAccountRequest = z.object({ confirmation: z.string().max(50) });
export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequest>;

/** 409 details when the user still owns workspaces (delete them or transfer ownership first). */
export const AccountDeletionBlocker = z.object({
  reason: z.literal('owns_workspaces'),
  workspaces: z.array(z.object({ id: Id, name: z.string() })),
});
export type AccountDeletionBlocker = z.infer<typeof AccountDeletionBlocker>;

export const DeleteAccountResponse = z.object({
  ok: z.literal(true),
  /** deleted = the user row was removed; anonymised = kept as an empty shell because records still reference it (e.g. co-signed decisions). */
  mode: z.enum(['deleted', 'anonymised']),
  workspacesLeft: z.number().int(),
});
export type DeleteAccountResponse = z.infer<typeof DeleteAccountResponse>;
